import { useMutation, useQuery } from "@tanstack/react-query";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import { dashboardKeys } from "../dashboard/queries";
import { getProviderConfigurations } from "../integrations/api";
import type { Schemas } from "../workspace/api.generated";
import * as reconciliationApi from "./api";
import type { IssueStatusFilter } from "./schema";

export const reconciliationKeys = {
  all: (epoch: number) => ["admin", epoch, "reconciliations"] as const,
  runs: (epoch: number) => [...reconciliationKeys.all(epoch), "runs"] as const,
  run: (epoch: number, runId: number) => [...reconciliationKeys.runs(epoch), runId] as const,
  issues: (epoch: number) => [...reconciliationKeys.all(epoch), "issues"] as const,
  issueList: (epoch: number, status: IssueStatusFilter) => [...reconciliationKeys.issues(epoch), status] as const,
  issue: (epoch: number, issueId: number) => [...reconciliationKeys.issues(epoch), "detail", issueId] as const,
  runIssues: (
    epoch: number,
    runId: number,
    query: { readonly status: IssueStatusFilter; readonly page: number; readonly size: number },
  ) => [...reconciliationKeys.run(epoch, runId), "issues", query] as const,
  providers: (epoch: number) => [...reconciliationKeys.all(epoch), "providers"] as const,
};

export function useReconciliationRun(runId: number) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: reconciliationKeys.run(session.sessionEpoch, runId),
    queryFn: ({ signal }) => reconciliationApi.getReconciliationRun(apiClient, runId, signal),
    staleTime: 0,
  });
}

export function useReconciliationRuns(enabled = true) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: reconciliationKeys.runs(session.sessionEpoch),
    queryFn: ({ signal }) => reconciliationApi.getReconciliationRuns(apiClient, signal),
    enabled,
    staleTime: 0,
  });
}

export function useReconciliationIssues(status: IssueStatusFilter, enabled = true) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: reconciliationKeys.issueList(session.sessionEpoch, status),
    queryFn: ({ signal }) => reconciliationApi.getReconciliationIssues(apiClient, status, signal),
    enabled,
    staleTime: 0,
  });
}

export function useReconciliationRunIssues(
  runId: number,
  query: { readonly status: IssueStatusFilter; readonly page: number; readonly size: number },
) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: reconciliationKeys.runIssues(session.sessionEpoch, runId, query),
    queryFn: ({ signal }) => reconciliationApi.getReconciliationRunIssues(apiClient, runId, query, signal),
    staleTime: 0,
  });
}

export function useReconciliationIssue(issueId?: number) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: reconciliationKeys.issue(session.sessionEpoch, issueId ?? 0),
    queryFn: ({ signal }) => reconciliationApi.getReconciliationIssue(apiClient, issueId!, signal),
    enabled: issueId !== undefined,
    staleTime: 0,
  });
}

export function useReconciliationProviders(enabled: boolean) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: reconciliationKeys.providers(session.sessionEpoch),
    queryFn: ({ signal }) => getProviderConfigurations(apiClient, {
      providerCode: "",
      serviceType: "",
      providerMode: "",
      active: "true",
      page: 0,
      size: 100,
    }, signal),
    enabled,
    staleTime: 0,
  });
}

export type ReconciliationMutation =
  | { readonly kind: "run"; readonly body: Schemas["RunReconciliationRequest"] }
  | { readonly kind: "resolve"; readonly issueId: number; readonly body: Schemas["ResolveReconciliationIssueRequest"] };

export function useReconciliationMutation() {
  const { apiClient, queryClient, sessionLifecycle } = useAdminApplication();
  const session = useAdminSession();
  return useMutation({
    mutationFn: async (input: ReconciliationMutation) => {
      const request = sessionLifecycle.createRequestController();
      try {
        return input.kind === "run"
          ? await reconciliationApi.runReconciliation(apiClient, input.body, request.controller.signal)
          : await reconciliationApi.resolveReconciliationIssue(apiClient, input.issueId, input.body, request.controller.signal);
      } finally {
        request.release();
      }
    },
    onSuccess: async (_result, input) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: reconciliationKeys.runs(session.sessionEpoch) }),
        queryClient.invalidateQueries({ queryKey: reconciliationKeys.issues(session.sessionEpoch) }),
        ...(input.kind === "run"
          ? [queryClient.invalidateQueries({ queryKey: dashboardKeys.detail(session.sessionEpoch, input.body.businessDate) })]
          : []),
      ]);
      if (input.kind === "resolve") {
        await queryClient.invalidateQueries({
          queryKey: reconciliationKeys.issue(session.sessionEpoch, input.issueId),
        });
      }
    },
  });
}
